import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  acceptInterestGroupProposal,
  addProposalInterest,
  createInterestGroupProposal,
  followInterestGroup,
  getInterestGroupProposals,
  getInterestGroups,
  leaveInterestGroup,
  rejectInterestGroupProposal,
  removeProposalInterest,
  InterestGroupPerson,
} from '../../../endpoints/interestGroupsTs';
import UserSearchModal, { User } from '../../../Components/UserSearchModal';

function personName(user: User): string {
  return (
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.id
  );
}

function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

const InterestGroupsPage: React.FC = () => {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [managers, setManagers] = useState<InterestGroupPerson[]>([]);
  const [interested, setInterested] = useState<InterestGroupPerson[]>([]);
  const [picker, setPicker] = useState<'manager' | 'interested' | null>(null);
  const [formError, setFormError] = useState('');
  const [rejectId, setRejectId] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [actionError, setActionError] = useState('');

  const groupsQuery = useApiGet(['interest-groups'], getInterestGroups, {
    retry: 1,
  });
  const proposalsQuery = useApiGet(
    ['interest-group-proposals'],
    getInterestGroupProposals,
    { retry: 1 }
  );
  const refresh = () => {
    groupsQuery.refetch();
    proposalsQuery.refetch();
  };

  const { mutate: saveProposal, isPending: saving } = useApiSend(
    () =>
      createInterestGroupProposal({
        name: name.trim(),
        description: description.trim(),
        manager_ids: managers.map((person) => person.user_id),
        interested_ids: interested.map((person) => person.user_id),
      }),
    () => {
      setSuggestOpen(false);
      refresh();
    },
    (err: unknown) => setFormError(messageOf(err))
  );

  const { mutate: follow } = useApiSend(
    (id: string) => followInterestGroup(id),
    () => refresh(),
    (err: unknown) => setActionError(messageOf(err))
  );
  const { mutate: leave } = useApiSend(
    (id: string) => leaveInterestGroup(id),
    () => refresh(),
    (err: unknown) => setActionError(messageOf(err))
  );
  const { mutate: markInterest } = useApiSend(
    (id: string) => addProposalInterest(id),
    () => refresh(),
    (err: unknown) => setActionError(messageOf(err))
  );
  const { mutate: clearInterest } = useApiSend(
    (id: string) => removeProposalInterest(id),
    () => refresh(),
    (err: unknown) => setActionError(messageOf(err))
  );
  const { mutate: accept } = useApiSend(
    (id: string) => acceptInterestGroupProposal(id),
    () => refresh(),
    (err: unknown) => setActionError(messageOf(err))
  );
  const { mutate: reject, isPending: rejecting } = useApiSend(
    () => rejectInterestGroupProposal(rejectId, rejectNote.trim()),
    () => {
      setRejectId('');
      setRejectNote('');
      refresh();
    },
    (err: unknown) => setActionError(messageOf(err))
  );

  const addPerson = (user: User) => {
    const person = { user_id: user.id, name: personName(user) };
    if (picker === 'manager') {
      setManagers((current) =>
        current.some((item) => item.user_id === person.user_id)
          ? current
          : [...current, person]
      );
      setInterested((current) =>
        current.filter((item) => item.user_id !== person.user_id)
      );
    } else if (picker === 'interested') {
      if (managers.some((item) => item.user_id === person.user_id)) {
        setPicker(null);
        return;
      }
      setInterested((current) =>
        current.some((item) => item.user_id === person.user_id)
          ? current
          : [...current, person]
      );
    }
    setPicker(null);
  };

  const openSuggest = () => {
    setName('');
    setDescription('');
    setManagers([]);
    setInterested([]);
    setFormError('');
    setSuggestOpen(true);
  };

  const groups = groupsQuery.data || [];
  const proposals = proposalsQuery.data?.proposals || [];
  const canReview = Boolean(proposalsQuery.data?.can_review);

  return (
    <Box sx={{ p: 2, maxWidth: 880 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Interest Groups</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openSuggest}
        >
          Suggest a group
        </Button>
      </Stack>
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError('')}
        >
          {actionError}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Groups
      </Typography>
      {groups.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          No special interest groups yet.
        </Typography>
      )}
      <Stack spacing={1} sx={{ mb: 4 }}>
        {groups.map((group) => (
          <Stack
            key={group.id}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={1}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Box>
              <Typography>{group.name}</Typography>
              {group.description && (
                <Typography variant="body2" color="text.secondary">
                  {group.description}
                </Typography>
              )}
            </Box>
            {group.role === 'none' ? (
              <Button onClick={() => follow(group.id)}>Follow</Button>
            ) : (
              <Button onClick={() => leave(group.id)}>
                {group.role === 'manager' ? 'Leave as manager' : 'Leave'}
              </Button>
            )}
          </Stack>
        ))}
      </Stack>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Proposals
      </Typography>
      {proposals.length === 0 && (
        <Typography color="text.secondary">No proposals to show.</Typography>
      )}
      <Stack spacing={1}>
        {proposals.map((proposal) => (
          <Box
            key={proposal.id}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{proposal.name}</Typography>
              <Chip size="small" label={proposal.status} />
            </Stack>
            {proposal.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {proposal.description}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Suggested by {proposal.suggested_by_name}
            </Typography>
            <Typography variant="body2">
              Managers:{' '}
              {proposal.managers.map((person) => person.name).join(', ') ||
                'None'}
            </Typography>
            <Typography variant="body2">
              Interested:{' '}
              {proposal.interested.map((person) => person.name).join(', ') ||
                'None'}
            </Typography>
            {proposal.review_note && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Note: {proposal.review_note}
              </Typography>
            )}
            {proposal.status === 'pending' && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {proposal.is_interested && (
                  <Button onClick={() => clearInterest(proposal.id)}>
                    Remove interest
                  </Button>
                )}
                {!proposal.is_interested && !proposal.is_manager && (
                  <Button onClick={() => markInterest(proposal.id)}>
                    I'm interested
                  </Button>
                )}
                {canReview && (
                  <>
                    <Button onClick={() => accept(proposal.id)}>Accept</Button>
                    <Button
                      color="inherit"
                      onClick={() => {
                        setRejectId(proposal.id);
                        setRejectNote('');
                      }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      <Dialog
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        fullWidth
      >
        <DialogTitle>Suggest a group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Managers
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {managers.map((person) => (
                  <Chip
                    key={person.user_id}
                    label={person.name}
                    onDelete={() =>
                      setManagers((current) =>
                        current.filter(
                          (item) => item.user_id !== person.user_id
                        )
                      )
                    }
                  />
                ))}
                <Button size="small" onClick={() => setPicker('manager')}>
                  Add manager
                </Button>
              </Stack>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Interested members
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {interested.map((person) => (
                  <Chip
                    key={person.user_id}
                    label={person.name}
                    onDelete={() =>
                      setInterested((current) =>
                        current.filter(
                          (item) => item.user_id !== person.user_id
                        )
                      )
                    }
                  />
                ))}
                <Button size="small" onClick={() => setPicker('interested')}>
                  Add member
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !name.trim()}
            onClick={() => saveProposal(undefined)}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <UserSearchModal
        modalOpen={picker !== null}
        setModalOpen={(open) => {
          if (!open) setPicker(null);
        }}
        userObjectRet={true}
        setUser={addPerson}
        statusFilter="active"
      />

      <Dialog
        open={Boolean(rejectId)}
        onClose={() => setRejectId('')}
        fullWidth
      >
        <DialogTitle>Reject proposal</DialogTitle>
        <DialogContent>
          <TextField
            label="Note"
            value={rejectNote}
            onChange={(event) => setRejectNote(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectId('')}>Cancel</Button>
          <Button
            variant="contained"
            disabled={rejecting}
            onClick={() => reject(undefined)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterestGroupsPage;
